import { useQueryClient, useMutation } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { useApprovePaymentRunLockMutation } from '~entities/payment/api/approve-payment-run-lock';
import { useUnlockPaymentRunMutation } from '~entities/payment/api/unlock-payment-run';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';
import { PaymentProcessingMethod } from '~entities/studios/model/types';
import type {
  ApprovePaymentRunLockParams,
  PaymentRunReviewRow,
} from '~entities/payment/api/reporting-payment-run-review';
import {
  downloadPaymentRunReviewCSV,
  postponePaymentRunAPI,
  useGetPaymentRunReviewReport,
} from '~entities/payment/api/reporting-payment-run-review';

import { useGetCurrency } from '~shared/api/currency-api';
import { QUERY_KEYS } from '~shared/api/query-keys';
import { formatMutationErrorMessage } from '~shared/utils/mutation-error-message';

import {
  PAYMENT_RUN_STATUS,
  POSTPONE_HOURS,
  POSTPONE_MAX_PER_CYCLE,
  type FilterTab,
} from './constants';
import {
  isPaymentRunApproveLockEnabled,
  isPaymentRunPostponeEnabled,
} from './payment-run-review-access';
import {
  firstOfMonth,
  formatCountdown,
  formatInstantInEastern,
  getNextRunDateString,
} from './payment-run-format';
import {
  isPaymentRunFailedTabStatus,
  isPaymentRunSuccessfulChargeTabStatus,
  isReportRowStatusReady,
  normalizeStatus,
} from './payment-run-status';
import {
  type PaymentRunReviewSortColumn,
  sortPaymentRunReviewRows,
} from './payment-run-review-sort';

type PaymentRunReviewSortDirection = 'asc' | 'desc';

const PAYMENT_RUN_REVIEW_STATE_KEY = 'payment-run-review-state';

const SORT_COLUMNS: ReadonlyArray<PaymentRunReviewSortColumn> = [
  'dueDate',
  'parent',
  'student',
  'classSession',
  'billingType',
  'baseAmount',
  'discount',
  'finalAmount',
  'transactionFee',
  'paidDate',
  'paidAmount',
  'payment',
  'status',
];

function parsePersistedFilterTab(value: unknown): FilterTab {
  if (
    value === 'all' ||
    value === 'ready' ||
    value === 'flagged' ||
    value === 'removed' ||
    value === 'failed' ||
    value === 'successfulCharge'
  ) {
    return value;
  }
  return 'all';
}

function isPaymentRunReviewSortColumn(
  value: unknown,
): value is PaymentRunReviewSortColumn {
  return (
    typeof value === 'string' &&
    (SORT_COLUMNS as ReadonlyArray<string>).includes(value)
  );
}

function initialSortFromPersisted(persisted: {
  sortColumn?: unknown;
  sortField?: unknown;
  sortDirection?: unknown;
}): {
  column: PaymentRunReviewSortColumn;
  direction: PaymentRunReviewSortDirection;
} {
  const direction: PaymentRunReviewSortDirection =
    persisted.sortDirection === 'desc' ? 'desc' : 'asc';
  if (isPaymentRunReviewSortColumn(persisted.sortColumn)) {
    return { column: persisted.sortColumn, direction };
  }
  if (persisted.sortField === 'parent') {
    return { column: 'parent', direction };
  }
  if (persisted.sortField === 'student') {
    return { column: 'student', direction };
  }
  return { column: 'dueDate', direction };
}

const formatDueDateLabel = (dueDate: string) => {
  const normalizedDueDate = /^\d{4}-\d{2}-\d{2}$/.test(dueDate)
    ? `${dueDate}T12:00:00`
    : dueDate;

  return new Date(normalizedDueDate).toLocaleDateString('en-US');
};

const readPaymentRunReviewState = (): {
  startDate?: string;
  endDate?: string;
  searchTerm?: string;
  appliedSearch?: string;
  filterTab?: FilterTab;
  tablePage?: number;
  tablePageSize?: number;
  sortColumn?: PaymentRunReviewSortColumn;
  /** Legacy session key from older builds; migrated in initialSortFromPersisted */
  sortField?: 'dueDate' | 'parent' | 'student';
  sortDirection?: PaymentRunReviewSortDirection;
} => {
  if (typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.sessionStorage.getItem(PAYMENT_RUN_REVIEW_STATE_KEY);
    return raw ? (JSON.parse(raw) as ReturnType<typeof readPaymentRunReviewState>) : {};
  } catch {
    return {};
  }
};

export function usePaymentRunReviewPage() {
  const persistedState = readPaymentRunReviewState();
  const initialSort = initialSortFromPersisted(persistedState);
  const [startDate, setStartDate] = useState<string>(() => {
    if (persistedState.startDate) {
      return persistedState.startDate;
    }
    const nextRun = getNextRunDateString();
    return firstOfMonth(nextRun);
  });
  const [endDate, setEndDate] = useState<string>(() =>
    persistedState.endDate ?? getNextRunDateString(),
  );
  const [searchTerm, setSearchTerm] = useState(persistedState.searchTerm ?? '');
  const [appliedSearch, setAppliedSearch] = useState(
    persistedState.appliedSearch ?? '',
  );
  const [filterTab, setFilterTab] = useState<FilterTab>(() =>
    parsePersistedFilterTab(persistedState.filterTab),
  );
  const [tablePage, setTablePage] = useState(persistedState.tablePage ?? 1);
  const [tablePageSize, setTablePageSize] = useState(
    persistedState.tablePageSize ?? 10,
  );
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null,
  );
  const [detailSheetRowAmounts, setDetailSheetRowAmounts] = useState<{
    baseAmount: number;
    discount: number;
    transactionFee: number;
    finalAmount: number;
  } | null>(null);
  const [postponeModalOpen, setPostponeModalOpen] = useState(false);
  const [postponePreviewTime, setPostponePreviewTime] = useState('');
  const [approveModalOpen, setApproveModalOpen] = useState(false);
  const [approveFromSelectionOnly, setApproveFromSelectionOnly] =
    useState(false);
  const [flaggedItemsAcknowledged, setFlaggedItemsAcknowledged] =
    useState(false);
  const [lockSelectionIds, setLockSelectionIds] = useState<Array<string>>([]);
  const [sortColumn, setSortColumn] = useState<PaymentRunReviewSortColumn>(
    () => initialSort.column,
  );
  const [sortDirection, setSortDirection] =
    useState<PaymentRunReviewSortDirection>(() => initialSort.direction);

  const params = useMemo(
    () => ({
      startDate,
      endDate,
      search: appliedSearch || undefined,
      page: 1,
      limit: 1000,
    }),
    [startDate, endDate, appliedSearch],
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.sessionStorage.setItem(
      PAYMENT_RUN_REVIEW_STATE_KEY,
      JSON.stringify({
        startDate,
        endDate,
        searchTerm,
        appliedSearch,
        filterTab,
        tablePage,
        tablePageSize,
        sortColumn,
        sortDirection,
      }),
    );
  }, [
    startDate,
    endDate,
    searchTerm,
    appliedSearch,
    filterTab,
    tablePage,
    tablePageSize,
    sortColumn,
    sortDirection,
  ]);

  const { data, isLoading, isError, refetch } = useGetPaymentRunReviewReport(
    params,
    { enabled: !!startDate && !!endDate },
  );

  const { data: studioDetails } = useGetStudioDetails();
  const paymentRunApproveLockEnabled = isPaymentRunApproveLockEnabled({
    paymentRunPostponeCronEnabled:
      studioDetails?.paymentRunPostponeCronEnabled,
    paymentProcessingMethod: studioDetails?.paymentProcessingMethod,
  });
  // Postpone only — not Approve & Lock / Unlock (ENR-2162).
  const paymentRunPostponeCronEnabled = isPaymentRunPostponeEnabled({
    paymentRunPostponeCronEnabled:
      studioDetails?.paymentRunPostponeCronEnabled,
    paymentProcessingMethod: studioDetails?.paymentProcessingMethod,
  });

  const nextRunAt = data?.schedule?.nextRunAt;
  const isPostponed = data?.schedule?.isPostponed === true;
  const isRunLocked = data?.schedule?.isLocked === true;
  const canUnlockRun = data?.schedule?.canUnlock === true;
  const approvePreview = data?.approveLockPreview;
  const approvedInvoiceIdsForRun = data?.schedule?.approvedInvoiceIds;
  const approvedForRunSet = useMemo(
    () =>
      new Set(
        (approvedInvoiceIdsForRun ?? [])
          .map((id) => String(id).trim())
          .filter(Boolean),
      ),
    [approvedInvoiceIdsForRun],
  );
  const incrementalLockAllowed =
    isRunLocked && approvedForRunSet.size > 0;
  const lockInteractionsDisabled = isRunLocked && !incrementalLockAllowed;
  const [countdown, setCountdown] = useState<string>('—');

  useEffect(() => {
    if (!nextRunAt) {
      setCountdown('—');
      return;
    }
    const tick = () => {
      setCountdown(formatCountdown(nextRunAt));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => {
      clearInterval(id);
    };
  }, [nextRunAt]);

  const queryClient = useQueryClient();
  const postponeMutation = useMutation({
    mutationFn: () => postponePaymentRunAPI({ hours: POSTPONE_HOURS }),
    onSuccess: () => {
      setPostponeModalOpen(false);
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PAYMENT_RUN_REVIEW(params),
      });
    },
  });

  const postponeCountUsed = Math.min(
    POSTPONE_MAX_PER_CYCLE,
    data?.schedule?.postponeCountUsed ?? 0,
  );
  const postponeCountMax =
    data?.schedule?.postponeCountMax ?? POSTPONE_MAX_PER_CYCLE;
  const postponeLimitReached = postponeCountUsed >= postponeCountMax;

  const postponeButtonState = useMemo(() => {
    if (!paymentRunPostponeCronEnabled) {
      return {
        disabled: true as const,
        title:
          'Payment run postponement is off for this studio (see Payment & Billing Settings).',
        statusHint: null as string | null,
      };
    }
    if (postponeLimitReached) {
      return {
        disabled: true as const,
        title: 'Maximum postponements reached for this cycle',
        statusHint: null as string | null,
      };
    }
    const rows = data?.rows ?? [];
    const readyRows = rows.filter((r) => isReportRowStatusReady(r.status));
    if (readyRows.length === 0) {
      return {
        disabled: true as const,
        title: 'No Ready charges in this report to postpone',
        statusHint:
          'Postpone applies to Ready charges that are not yet approved for this run.',
      };
    }
    const hasDeferrableReady = readyRows.some((r) => {
      if (!isRunLocked) return true;
      if (lockInteractionsDisabled) return false;
      const id = String(r.id).trim();
      return !approvedForRunSet.has(id);
    });
    if (!hasDeferrableReady) {
      return {
        disabled: true as const,
        title:
          'Every Ready charge is already approved for this automatic run',
        statusHint:
          'Nothing left to postpone until new Ready charges appear or you unlock the run.',
      };
    }
    return {
      disabled: false as const,
      title: undefined as string | undefined,
      statusHint: null as string | null,
    };
  }, [
    paymentRunPostponeCronEnabled,
    postponeLimitReached,
    data?.rows,
    isRunLocked,
    lockInteractionsDisabled,
    approvedForRunSet,
  ]);

  const openPostponeModal = useCallback(() => {
    postponeMutation.reset();
    const now = Date.now();
    const nextMs = nextRunAt ? new Date(nextRunAt).getTime() : NaN;
    const isSecondPostpone =
      postponeCountUsed >= 1 &&
      isPostponed &&
      !Number.isNaN(nextMs) &&
      nextMs > now;
    const previewMs = isSecondPostpone
      ? nextMs + POSTPONE_HOURS * 60 * 60 * 1000
      : now + POSTPONE_HOURS * 60 * 60 * 1000;
    setPostponePreviewTime(formatInstantInEastern(previewMs));
    setPostponeModalOpen(true);
  }, [
    postponeMutation,
    nextRunAt,
    postponeCountUsed,
    isPostponed,
  ]);

  const approveLockMutation = useApprovePaymentRunLockMutation({
    reportParams: params,
  });

  const unlockMutation = useUnlockPaymentRunMutation({
    reportParams: params,
  });

  const approveModalError = approveLockMutation.isError
    ? formatMutationErrorMessage(
        approveLockMutation.error,
        'Failed to approve',
      )
    : '';

  const handleConfirmPostpone = useCallback(() => {
    postponeMutation.mutate();
  }, [postponeMutation]);

  const postponeModalError = postponeMutation.isError
    ? formatMutationErrorMessage(
        postponeMutation.error,
        'Failed to postpone run',
      )
    : '';

  const { data: currencyData } = useGetCurrency();
  const symbol = currencyData?.symbol ?? '$';

  const handleSearch = useCallback(() => {
    setAppliedSearch(searchTerm.trim());
    setTablePage(1);
  }, [searchTerm]);

  const lockableIds = useMemo(() => {
    const rows = data?.rows ?? [];
    return new Set(
      rows
        .filter((r) => {
          const s = normalizeStatus(r.status);
          return (
            s === PAYMENT_RUN_STATUS.READY || s === PAYMENT_RUN_STATUS.FEE_CAP
          );
        })
        .map((r) => r.id),
    );
  }, [data?.rows]);

  const selectedLockableSorted = useMemo(
    () =>
      [...new Set(lockSelectionIds)].filter((id) => lockableIds.has(id)).sort(),
    [lockSelectionIds, lockableIds],
  );

  const invoiceIdsPendingApprove = useMemo(
    () =>
      selectedLockableSorted.filter(
        (id) => !approvedForRunSet.has(String(id).trim()),
      ),
    [selectedLockableSorted, approvedForRunSet],
  );

  /** Any non-empty checkbox selection is a selective lock (always sends IDs). */
  const isSelectiveLockApprove = selectedLockableSorted.length > 0;

  const isManualReviewStudio =
    studioDetails?.paymentProcessingMethod === PaymentProcessingMethod.MANUAL;

  const openApproveModal = useCallback(() => {
    // Manual Review must always lock a concrete invoice list — empty
    // selection used to clear paymentRunApprovedInvoiceIds and let cron
    // charge every due scheduled invoice (ENR-2159).
    if (isManualReviewStudio && selectedLockableSorted.length === 0) {
      toast.error(
        'Select Ready charges to Approve & Lock on Manual Review studios.',
      );
      return;
    }
    approveLockMutation.reset();
    setFlaggedItemsAcknowledged(false);
    // If rows are checked, toolbar Approve must lock that selection (send
    // invoiceIds). Treating "all loaded Ready rows selected" as a full-run
    // omit-IDs lock previously charged every due invoice via cron (ENR-2159).
    setApproveFromSelectionOnly(selectedLockableSorted.length > 0);
    setApproveModalOpen(true);
  }, [
    approveLockMutation,
    isManualReviewStudio,
    selectedLockableSorted.length,
  ]);

  const lockSelectionApprovePreview = useMemo(() => {
    if (invoiceIdsPendingApprove.length === 0 || !data?.rows) return null;
    const set = new Set(invoiceIdsPendingApprove);
    const rows = data.rows.filter((r) => set.has(r.id));
    let chargesToProcess = 0;
    let totalAmount = 0;
    let flaggedCount = 0;
    let flaggedAmount = 0;
    const methods = new Set<string>();
    for (const r of rows) {
      const s = normalizeStatus(r.status);
      chargesToProcess += 1;
      totalAmount += r.finalAmount;
      if (s === PAYMENT_RUN_STATUS.FEE_CAP) {
        flaggedCount += 1;
        flaggedAmount += r.finalAmount;
      }
      const pm = r.paymentMethod.trim();
      if (pm) methods.add(pm);
    }
    const paymentMethodsSummary =
      methods.size === 0 ? '—' : [...methods].sort().join(', ');
    return {
      chargesToProcess,
      totalAmount,
      flaggedCount,
      flaggedAmount,
      paymentMethodsSummary,
    };
  }, [data?.rows, invoiceIdsPendingApprove]);

  const idsForFooterApproveStats = incrementalLockAllowed
    ? invoiceIdsPendingApprove
    : selectedLockableSorted;

  const tableFooterSelectionStats = useMemo(() => {
    const rows = data?.rows ?? [];
    const idSet = new Set(idsForFooterApproveStats);
    let selectedSum = 0;
    let flaggedInSelection = 0;
    for (const r of rows) {
      if (!idSet.has(r.id)) continue;
      selectedSum += Number(r.finalAmount) || 0;
      if (normalizeStatus(r.status) === PAYMENT_RUN_STATUS.FEE_CAP) {
        flaggedInSelection += 1;
      }
    }
    return { selectedSum, flaggedInSelection };
  }, [data?.rows, idsForFooterApproveStats]);

  const openApproveModalForSelectedRows = useCallback(() => {
    if (
      lockSelectionApprovePreview == null ||
      invoiceIdsPendingApprove.length === 0
    ) {
      return;
    }
    approveLockMutation.reset();
    setFlaggedItemsAcknowledged(false);
    setApproveFromSelectionOnly(true);
    setApproveModalOpen(true);
  }, [
    approveLockMutation,
    lockSelectionApprovePreview,
    invoiceIdsPendingApprove.length,
  ]);

  const approveModalPreview =
    approveFromSelectionOnly || isSelectiveLockApprove
      ? lockSelectionApprovePreview
      : approvePreview
        ? {
            chargesToProcess: approvePreview.chargesToProcess,
            totalAmount: approvePreview.totalAmount,
            flaggedCount: approvePreview.flaggedCount,
            flaggedAmount: approvePreview.flaggedAmount,
            paymentMethodsSummary: approvePreview.paymentMethodsSummary,
          }
        : null;

  // Always send invoiceIds when a selection is active — never omit IDs just
  // because every loaded lockable row is checked (that used to mean "full run").
  const submitApproveUsesInvoiceIds =
    approveFromSelectionOnly || isSelectiveLockApprove;

  const approveModalFlaggedCount = approveModalPreview?.flaggedCount ?? 0;
  const approveModalCanSubmit =
    approveModalFlaggedCount === 0 || flaggedItemsAcknowledged;

  useEffect(() => {
    setLockSelectionIds([]);
  }, [startDate, endDate, appliedSearch]);

  const filteredRows = useMemo(() => {
    const rows = data?.rows ?? [];
    const normalizedSearch = appliedSearch.trim().toLowerCase();

    const tabFilteredRows =
      filterTab === 'all'
        ? rows.filter(
            (r) => !isPaymentRunSuccessfulChargeTabStatus(r.status),
          )
        : filterTab === 'ready'
          ? rows.filter(
              (r) => normalizeStatus(r.status) === PAYMENT_RUN_STATUS.READY,
            )
          : filterTab === 'flagged'
            ? rows.filter(
                (r) => normalizeStatus(r.status) === PAYMENT_RUN_STATUS.FEE_CAP,
              )
            : filterTab === 'failed'
              ? rows.filter((r) => isPaymentRunFailedTabStatus(r.status))
              : filterTab === 'successfulCharge'
                ? rows.filter((r) =>
                    isPaymentRunSuccessfulChargeTabStatus(r.status),
                  )
                : rows.filter((r) => {
                    const s = normalizeStatus(r.status);
                    return (
                      s === PAYMENT_RUN_STATUS.DROPPED ||
                      s === PAYMENT_RUN_STATUS.DUPLICATE ||
                      s === PAYMENT_RUN_STATUS.SUPERSEDED
                    );
                  });

    const searchedRows = !normalizedSearch
      ? tabFilteredRows
      : tabFilteredRows.filter((row) => {
          const dueDateLabel = row.dueDate
            ? formatDueDateLabel(row.dueDate)
            : '';
          const haystack = [
            row.parent,
            row.student,
            row.dueDate,
            dueDateLabel,
          ]
            .join(' ')
            .toLowerCase();
          return haystack.includes(normalizedSearch);
        });

    return searchedRows;
  }, [data?.rows, filterTab, appliedSearch]);

  const sortedFilteredRows = useMemo(
    () => sortPaymentRunReviewRows(filteredRows, sortColumn, sortDirection),
    [filteredRows, sortColumn, sortDirection],
  );

  const requestSort = useCallback(
    (column: PaymentRunReviewSortColumn) => {
      setTablePage(1);
      if (sortColumn === column) {
        setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortColumn(column);
        setSortDirection('asc');
      }
    },
    [sortColumn],
  );

  const filteredTotal = sortedFilteredRows.length;
  const tableTotalPages = Math.max(1, Math.ceil(filteredTotal / tablePageSize));
  const tablePageSafe = Math.min(Math.max(1, tablePage), tableTotalPages);
  const paginatedRows = useMemo(() => {
    const start = (tablePageSafe - 1) * tablePageSize;
    return sortedFilteredRows.slice(start, start + tablePageSize);
  }, [sortedFilteredRows, tablePageSafe, tablePageSize]);

  const lockableFilteredRows = useMemo(
    () => sortedFilteredRows.filter((r) => lockableIds.has(r.id)),
    [sortedFilteredRows, lockableIds],
  );

  const lockableSelectableForBulk = useMemo(
    () =>
      incrementalLockAllowed
        ? lockableFilteredRows.filter(
            (r) => !approvedForRunSet.has(String(r.id).trim()),
          )
        : lockableFilteredRows,
    [incrementalLockAllowed, lockableFilteredRows, approvedForRunSet],
  );

  const toggleLockSelection = useCallback(
    (invoiceId: string, checked: boolean) => {
      setLockSelectionIds((prev) => {
        if (checked) {
          if (prev.includes(invoiceId)) return prev;
          return [...prev, invoiceId];
        }
        return prev.filter((id) => id !== invoiceId);
      });
    },
    [],
  );

  const toggleSelectAllLockableInFilter = useCallback(() => {
    const ids = lockableSelectableForBulk.map((r) => r.id);
    setLockSelectionIds((prev) => {
      const allOn =
        ids.length > 0 && ids.every((id) => prev.includes(id));
      if (allOn) {
        return prev.filter((id) => !ids.includes(id));
      }
      return [...new Set([...prev, ...ids])];
    });
  }, [lockableSelectableForBulk]);

  const lockHeaderAllChecked =
    lockableSelectableForBulk.length > 0 &&
    lockableSelectableForBulk.every((r) => lockSelectionIds.includes(r.id));
  const lockHeaderIndeterminate =
    lockableSelectableForBulk.some((r) => lockSelectionIds.includes(r.id)) &&
    !lockHeaderAllChecked;

  const summary = data?.summary;
  const readyCount = summary?.readyCount;
  const readySum = summary?.readySum;
  const flaggedCount = summary?.flaggedCount;
  const flaggedSum = summary?.flaggedSum;
  const removedCount = summary?.removedCount;
  const removedSum = summary?.removedSum;
  const failedCount = summary?.failedCount;
  const successfulChargeCount = summary?.successfulChargeCount;
  const totalCount = summary?.totalCount;
  /** Rows under "All" tab; prefer API `allFilterCount`, else approximate for older BE. */
  const allFilterCount =
    summary?.allFilterCount ??
    (totalCount != null && successfulChargeCount != null
      ? Math.max(0, totalCount - successfulChargeCount)
      : totalCount);
  const netSum = summary?.netSum;
  const attentionCount =
    (flaggedCount ?? 0) + (removedCount ?? 0) + (failedCount ?? 0);

  const handleDownloadCSV = useCallback(async () => {
    if (!startDate || !endDate) return;
    try {
      const blob = await downloadPaymentRunReviewCSV({
        startDate,
        endDate,
        search: appliedSearch || undefined,
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payment-run-review-${startDate}-${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (e) {
      console.error('CSV download failed:', e);
    }
  }, [startDate, endDate, appliedSearch]);

  const handleApproveModalOpenChange = useCallback((open: boolean) => {
    setApproveModalOpen(open);
    if (!open) {
      setFlaggedItemsAcknowledged(false);
      setApproveFromSelectionOnly(false);
    }
  }, []);

  const handleSubmitApprove = useCallback(
    (payload: ApprovePaymentRunLockParams) => {
      approveLockMutation.mutate(payload, {
        onSuccess: () => {
          setApproveModalOpen(false);
          setFlaggedItemsAcknowledged(false);
          setApproveFromSelectionOnly(false);
          setLockSelectionIds([]);
        },
      });
    },
    [approveLockMutation],
  );

  const handleDetailSheetOpenChange = useCallback((open: boolean) => {
    setDetailSheetOpen(open);
    if (!open) {
      setSelectedInvoiceId(null);
      setDetailSheetRowAmounts(null);
    }
  }, []);

  const openRowDetail = useCallback((row: PaymentRunReviewRow) => {
    setSelectedInvoiceId(row.id);
    setDetailSheetRowAmounts({
      baseAmount: row.baseAmount,
      discount: row.discount,
      transactionFee: row.transactionFee,
      finalAmount: row.finalAmount,
    });
    setDetailSheetOpen(true);
  }, []);

  return {
    approveDialog: {
      open: approveModalOpen,
      onOpenChange: handleApproveModalOpenChange,
      nextRunAt,
      currencySymbol: symbol,
      approveModalPreview,
      showSelectiveCopy: isSelectiveLockApprove || approveFromSelectionOnly,
      selectedLockableCount: selectedLockableSorted.length,
      flaggedItemsAcknowledged,
      onFlaggedItemsAcknowledgedChange: setFlaggedItemsAcknowledged,
      approveModalError,
      isApprovePending: approveLockMutation.isPending,
      approveModalCanSubmit,
      submitApproveUsesInvoiceIds,
      invoiceIdsPendingApprove,
      startDate,
      endDate,
      approveModalFlaggedCount,
      onSubmitApprove: handleSubmitApprove,
    },
    postponeDialog: {
      open: postponeModalOpen,
      onOpenChange: setPostponeModalOpen,
      incrementalLockAllowed,
      postponePreviewTime,
      postponeCountMax,
      postponeCountUsed,
      postponeModalError,
      isPostponePending: postponeMutation.isPending,
      postponeButtonState,
      onConfirmPostpone: handleConfirmPostpone,
    },
    detailSheet: {
      open: detailSheetOpen,
      onOpenChange: handleDetailSheetOpenChange,
      invoiceId: selectedInvoiceId,
      rowAmounts: detailSheetRowAmounts,
      startDate,
      endDate,
      currencySymbol: symbol,
      reportQueryParams: params,
    },
    toolbar: {
      studioName: data?.studioName,
      startDate,
      setStartDate,
      endDate,
      setEndDate,
      nextRunAt,
      countdown,
      readyCount,
      readySum,
      flaggedCount,
      flaggedSum,
      removedCount,
      removedSum,
      totalCount,
      netSum,
      currencySymbol: symbol,
      postponeButtonState,
      postponeLimitReached,
      postponeCountMax,
      openPostponeModal,
      handleDownloadCSV,
      lockInteractionsDisabled,
      incrementalLockAllowed,
      approvePreview,
      openApproveModal,
      canUnlockRun,
      paymentRunApproveLockEnabled,
      paymentRunPostponeCronEnabled,
      unlockMutation,
      isRunLocked,
      approvedInvoiceIdsForRun,
      isPostponed,
    },
    filters: {
      filterTab,
      setFilterTab,
      setTablePage,
      allFilterCount,
      readyCount,
      flaggedCount,
      removedCount,
      failedCount,
      successfulChargeCount,
      attentionCount,
      searchTerm,
      setSearchTerm,
      setAppliedSearch,
      handleSearch,
    },
    table: {
      isLoading,
      isError,
      refetch,
      lockHeaderAllChecked,
      lockHeaderIndeterminate,
      toggleSelectAllLockableInFilter,
      lockInteractionsDisabled,
      lockableSelectableForBulk,
      paginatedRows,
      lockSelectionIds,
      incrementalLockAllowed,
      approvedForRunSet,
      lockableIds,
      toggleLockSelection,
      openRowDetail,
      isPostponed,
      currencySymbol: symbol,
      filteredTotal,
      tablePageSize,
      setTablePageSize,
      tablePageSafe,
      tableTotalPages,
      setTablePage,
      idsForFooterApproveStats,
      tableFooterSelectionStats,
      invoiceIdsPendingApprove,
      nextRunAt,
      lockSelectionApprovePreview,
      openApproveModalForSelectedRows,
      paymentRunApproveLockEnabled,
      paymentRunPostponeCronEnabled,
      sortColumn,
      sortDirection,
      onRequestSort: requestSort,
    },
  };
}
